import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { combineLatest } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ManagerSettingsService } from '../../security-deposit/common/services/manager-settings.service';
import { SdDestination, canOpenSdDestination } from '../../security-deposit/common/models/sd-navigation';
import { environment } from 'src/environments/environment';

interface NavItem {
  label: string;
  icon?: string;
  svgIcon?: string;
  route: string;
  exact?: boolean;
  queryParams?: Record<string, any>;
  isSubItem?: boolean;
  destination: SdDestination;
  /** Only show this item in non-production builds. */
  devOnly?: boolean;
}

const CUSTOM_SVG_ICONS: Record<string, string> = {
  reports: './assets/images/reports-icon.svg',
  ranking: './assets/images/ranking-icon.svg',
};

interface NavGroup {
  items: NavItem[];
}

const ALL_NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { label: 'Invites',      icon: 'mail_outline',         route: '/security-deposit/invites',      destination: 'invites' },
      { label: 'Applications', icon: 'assignment',           route: '/security-deposit/applications', destination: 'applications' },
      { label: 'Residents',    icon: 'people_outline',       route: '/security-deposit/residents',    destination: 'residents' },
      { label: 'Active',       icon: 'check_circle_outline', route: '/security-deposit/residents', queryParams: { residentGroup: 1 }, isSubItem: true, destination: 'residents' },
      { label: 'Lease Ended',  icon: 'event_busy',           route: '/security-deposit/residents', queryParams: { residentGroup: 2 }, isSubItem: true, destination: 'residents' },
      { label: 'Opt-Out',      icon: 'do_not_disturb_on',   route: '/security-deposit/residents', queryParams: { residentGroup: 5 }, isSubItem: true, destination: 'residents' },
      { label: 'Past',         icon: 'inventory_2',          route: '/security-deposit/residents', queryParams: { residentGroup: 4 }, isSubItem: true, destination: 'residents' },
      { label: 'Claims',       icon: 'gavel',                route: '/security-deposit/claims',       destination: 'claims' },
    ]
  },
  {
    items: [
      { label: 'Adoption Leaderboard', svgIcon: 'ranking',      route: '/security-deposit/rankings',     destination: 'rankings' },
      { label: 'Properties',           icon: 'apartment',       route: '/security-deposit/properties',   destination: 'properties' },
      { label: 'Reports',              svgIcon: 'reports',      route: '/security-deposit/reports',      destination: 'reports', devOnly: true },
      { label: 'Invite Links',         icon: 'link',            route: '/security-deposit/invite-links', destination: 'invites' },
      { label: 'Managers',             icon: 'manage_accounts', route: '/security-deposit/managers',     destination: 'managers' },
    ]
  },
  {
    items: [
      { label: 'Finance', icon: 'payments', route: '/security-deposit/finances', destination: 'finances' },
    ]
  }
];

@Component({
  standalone: false,
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  private readonly managerSettingsService = inject(ManagerSettingsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);

  navGroups: NavGroup[] = [];

  constructor() {
    Object.entries(CUSTOM_SVG_ICONS).forEach(([name, path]) =>
      this.iconRegistry.addSvgIcon(name, this.sanitizer.bypassSecurityTrustResourceUrl(path))
    );
  }

  ngOnInit(): void {
    combineLatest([this.managerSettingsService.settings$, this.managerSettingsService.rankingsEnabled$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([settings, rankingsEnabled]) => {
        this.navGroups = ALL_NAV_GROUPS.map(group => ({
          items: group.items.filter(item =>
            (!item.devOnly || !environment.production) &&
            canOpenSdDestination(settings?.rights, item.destination, rankingsEnabled)
          )
        })).filter(group => group.items.length > 0);
      });
    this.managerSettingsService.getAccess().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ error: () => this.navGroups = [] });
  }
}
